import { Collapse, Divider, Stack, SxProps, Theme } from "@mui/material";
import { SenecCard } from "src/components/cards/base/SenecCard";
import { SenecFunctionalButton } from "src/components/button/senec-functional-button/SenecFunctionalButton";
import { ChevronDownIcon, ChevronUpIcon } from "src/foundations/icons";
import { ReactNode, useCallback, useState } from "react";

export interface ExpandableCardProps {
    /**
     * Slot for the header area of the card. Can be used to insert contents like title, subtitle or action buttons.
     */
    headerSlot?: ReactNode;
    /**
     * Slot for the detail area of the card. This area is collapsible and used to display additional information or content that can be expanded as needed.
     */
    detailSlot?: ReactNode;
    /**
     * Text displayed on the toggle button when the detail area is visible.
     */
    showLessText: string;
    /**
     * Text displayed on the toggle button when the detail area is hidden.
     */
    showMoreText: string;
    /**
     * The initial rendering of the component. Default state is true if not specified.
     */
    isContentVisibleByDefault?: boolean;
    /**
     * Optional styling properties.
     */
    sx?: SxProps<Theme>;
}

export const ExpandableCard = ({
    headerSlot,
    detailSlot,
    showLessText,
    showMoreText,
    isContentVisibleByDefault = true,
    sx,
}: ExpandableCardProps) => {
    const [isContentVisible, setContentVisibility] = useState<boolean>(isContentVisibleByDefault);

    const toggleContent = useCallback(() => {
        setContentVisibility((isVisible) => !isVisible);
    }, []);

    return (
        <SenecCard sx={sx}>
            {headerSlot}
            <Collapse in={isContentVisible}>
                <Stack>{detailSlot}</Stack>
            </Collapse>
            <Stack width={"100%"} spacing={3} marginTop={3}>
                <Divider />
                <ToggleContentButton
                    isContentVisible={isContentVisible}
                    toggleContent={toggleContent}
                    showLessText={showLessText}
                    showMoreText={showMoreText}
                />
            </Stack>
        </SenecCard>
    );
};

interface ToggleContentButtonProps {
    isContentVisible: boolean;
    toggleContent: () => void;
    showLessText: string;
    showMoreText: string;
}
const ToggleContentButton = ({ isContentVisible, toggleContent, showLessText, showMoreText }: ToggleContentButtonProps) => {
    return (
        <SenecFunctionalButton level="secondary" onClick={toggleContent} data-testid={"toggle-content-btn"}>
            {isContentVisible ? (
                <>
                    {showLessText}&nbsp;
                    <ChevronUpIcon />
                </>
            ) : (
                <>
                    {showMoreText}&nbsp;
                    <ChevronDownIcon />
                </>
            )}
        </SenecFunctionalButton>
    );
};
